import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Video, CheckCircle, XCircle, AlertCircle, ExternalLink, RefreshCw, 
  Shield, Copy, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleMeetSetup() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUrl, setAuthUrl] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/examiner/google-meet/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch Google Meet status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchStatus();
    }
  }, [token]);

  const getAuthUrl = async () => {
    try {
      const res = await axios.get(`${API}/admin/google-meet/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthUrl(res.data);
    } catch (err) {
      toast.error('Failed to generate authorization URL');
    }
  };

  const revokeAuth = async () => {
    if (!window.confirm('Are you sure you want to revoke Google Meet authorization?')) return;
    
    try {
      await axios.post(`${API}/admin/google-meet/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Authorization revoked');
      setAuthUrl(null);
      setTestResult(null);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to revoke authorization');
    }
  };

  const testIntegration = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/google-meet/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Google Meet integration is working!');
      } else {
        toast.warning('Test meeting created with placeholder mode');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
      setTestResult({ success: false, error: err.response?.data?.detail });
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Loading Google Meet status...</div>;
  }

  return (
    <div className="space-y-6" data-testid="google-meet-setup">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-green-100 flex items-center justify-center">
            <Video className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1B2A4A]">Google Meet Integration</h3>
            <p className="text-sm text-slate-500">Create video meeting links for Viva examinations</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchStatus}
          className="rounded-sm"
        >
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <Separator />

      {/* Status Overview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Integration Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              {status?.libraries_installed ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm">API Libraries</span>
            </div>
            <div className="flex items-center gap-2">
              {status?.credentials_file_exists ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm">Credentials File</span>
            </div>
            <div className="flex items-center gap-2">
              {status?.is_authorized ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              <span className="text-sm">OAuth Authorized</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={status?.mode === 'google_meet' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {status?.mode === 'google_meet' ? 'Active' : 'Placeholder'}
              </Badge>
            </div>
          </div>

          <div className="p-3 bg-slate-50 rounded-sm">
            <p className="text-sm text-slate-600">{status?.message}</p>
          </div>
        </CardContent>
      </Card>

      {/* OAuth Setup Section */}
      {!status?.is_authorized && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-600" />
              OAuth Authorization Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Setup Required</AlertTitle>
              <AlertDescription>
                To create real Google Meet links automatically, you need to authorize IMCAC to access Google Meet API.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Setup Instructions:</p>
              <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                <li>Click "Generate Authorization URL" below</li>
                <li>
                  <strong>Important:</strong> First, add this redirect URI to your Google Cloud Console:
                  <br />
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded mt-1 block">
                    {process.env.REACT_APP_BACKEND_URL}/api/admin/google-meet/callback
                  </code>
                  <br />
                  Go to <strong>Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client IDs → Edit → Add Authorized redirect URIs</strong>
                </li>
                <li>Click the authorization URL and sign in with your Google account</li>
                <li>Grant permission for IMCAC to create meetings</li>
                <li>You'll be redirected back with a success message</li>
              </ol>

              {!authUrl ? (
                <Button onClick={getAuthUrl} className="bg-[#1B2A4A] text-white rounded-sm">
                  <Settings className="h-4 w-4 mr-2" /> Generate Authorization URL
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-white border rounded-sm">
                    <p className="text-xs text-slate-500 mb-1">Authorization URL:</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-blue-600 break-all flex-1">
                        {authUrl.authorization_url?.substring(0, 100)}...
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(authUrl.authorization_url)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild className="bg-green-600 text-white rounded-sm">
                      <a href={authUrl.authorization_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" /> Open Authorization URL
                      </a>
                    </Button>
                    <Button variant="outline" onClick={() => setAuthUrl(null)} className="rounded-sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Section */}
      {status?.is_authorized && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Test Integration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Test the Google Meet integration by creating a test meeting link.
            </p>

            <div className="flex gap-2">
              <Button 
                onClick={testIntegration} 
                disabled={testing}
                className="bg-[#1B2A4A] text-white rounded-sm"
              >
                {testing ? 'Testing...' : 'Create Test Meeting'}
              </Button>
              <Button 
                variant="outline" 
                onClick={revokeAuth}
                className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
              >
                Revoke Authorization
              </Button>
            </div>

            {testResult && (
              <div className={`p-4 rounded-sm ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? 'Test Successful!' : 'Test Failed'}
                  </span>
                </div>
                {testResult.meeting && (
                  <div className="space-y-2 text-sm">
                    <p className="text-slate-600">
                      <strong>Provider:</strong> {testResult.meeting.provider}
                    </p>
                    <p className="text-slate-600">
                      <strong>Meeting URI:</strong>{' '}
                      <a href={testResult.meeting.meeting_uri} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                        {testResult.meeting.meeting_uri}
                      </a>
                    </p>
                    <p className="text-slate-600">
                      <strong>Meeting Code:</strong> {testResult.meeting.meeting_code}
                    </p>
                  </div>
                )}
                {testResult.error && (
                  <p className="text-sm text-red-600">{testResult.error}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600">
            <p>Once authorized, IMCAC will automatically:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Create unique Google Meet links for each scheduled Viva examination</li>
              <li>Send meeting links to candidates and examiners via email notifications</li>
              <li>Display meeting links in the Viva Schedule dashboard</li>
            </ul>
            <Separator className="my-4" />
            <p className="text-xs text-slate-500">
              <strong>Note:</strong> If not authorized, placeholder links will be generated that examiners can manually replace with real Google Meet links.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
